/**
 * Buttons an agent attaches to a reply (ant's MessageAction): what each kind
 * asks for and the checks ant makes, run here first so the agent sees them
 * on the field instead of as a failed send.
 */

import {
  ChatActionKind,
  ChatMessageActionInput,
  RiderRental,
} from '../../hooks/chat/api';
import { dayKey, formatDateTime, formatTime } from './format';

export const MAX_ACTIONS = 4;
export const MAX_LABEL_LENGTH = 40;
export const MAX_VALUE_LENGTH = 500;

export interface ActionKindInfo {
  kind: ChatActionKind;
  /** The menu entry. */
  title: string;
  /** What the rider's tap does, under the menu entry. */
  description: string;
  /** The button text the form starts with. */
  defaultLabel: string;
  needsRental: boolean;
}

export const ACTION_KINDS: ActionKindInfo[] = [
  {
    kind: 'reply',
    title: 'Quick reply',
    description: 'The rider taps to answer with a ready-made message',
    defaultLabel: '',
    needsRental: false,
  },
  {
    kind: 'open_rental',
    title: 'Open rental',
    description: "Opens the rider's rental screen",
    defaultLabel: 'Open my rental',
    needsRental: true,
  },
  {
    kind: 'retry_unlock',
    title: 'Retry unlock',
    description: 'Unlocks when the rider taps it, like the Unlock button on their rental',
    defaultLabel: 'Try unlocking again',
    needsRental: true,
  },
  {
    kind: 'end_rental',
    title: 'End rental',
    description: 'Opens the end-rental flow',
    defaultLabel: 'End my rental',
    needsRental: true,
  },
  {
    kind: 'add_payment_method',
    title: 'Add payment method',
    description: 'Opens the payment screen',
    defaultLabel: 'Add a payment method',
    needsRental: false,
  },
  {
    kind: 'open_url',
    title: 'Open link',
    description: 'Opens an https link',
    defaultLabel: 'Open link',
    needsRental: false,
  },
];

export const actionKindInfo = (kind: string): ActionKindInfo | undefined =>
  ACTION_KINDS.find((k) => k.kind === kind);

/** What the form edits; every field a string so inputs stay controlled. */
export interface ActionDraft {
  kind: ChatActionKind;
  label: string;
  value: string;
  rentalId: string;
}

export type ActionErrors = Partial<Record<'label' | 'value' | 'rentalId', string>>;

/** A new draft of `kind`, the rental prefilled with the one the chat is anchored to. */
export function newActionDraft(
  kind: ChatActionKind,
  anchoredRentalId: string | null | undefined
): ActionDraft {
  const info = actionKindInfo(kind);
  return {
    kind,
    label: info?.defaultLabel ?? '',
    value: '',
    rentalId: info?.needsRental ? anchoredRentalId ?? '' : '',
  };
}

/** A rental a button can act on, as the form's picker lists it. */
export interface RentalChoice {
  id: string;
  label: string;
  ended: boolean;
}

/** "1:21 AM" today, "Sep 24, 6:05 PM" on another day. */
const whenLabel = (iso: string, now: Date) =>
  dayKey(iso) === dayKey(now.toISOString()) ? formatTime(iso) : formatDateTime(iso, now);

/**
 * "Kayak #71021 · Ann Arbor · started 1:21 AM": the rental as CS knows it.
 * The hardware or system may be unreadable to this admin; the number stays.
 */
export function rentalLabel(rental: RiderRental, now: Date = new Date()): string {
  const { hardware } = rental;
  const unit = hardware ? hardware.numberText ?? hardware.number : null;
  const what = hardware
    ? [
        hardware.hardwareProduct?.name ?? 'Hardware',
        unit != null && unit !== '' ? `#${unit}` : null,
      ]
        .filter(Boolean)
        .join(' ')
    : `Rental ${rental.oldId ?? rental.id.slice(0, 8)}`;
  const when = rental.endTime
    ? `ended ${whenLabel(rental.endTime, now)}`
    : rental.startTime
    ? `started ${whenLabel(rental.startTime, now)}`
    : null;
  return [what, rental.system?.name, when].filter(Boolean).join(' · ');
}

/**
 * What a rental kind's picker offers: the rider's active rentals, and for
 * "Open rental" the anchored one too once it has ended (the rider can still
 * look at it; there is nothing left to unlock or end).
 */
export function rentalChoices(
  kind: ChatActionKind,
  active: RiderRental[],
  endedAnchor: RiderRental | null,
  now: Date = new Date()
): RentalChoice[] {
  if (!actionKindInfo(kind)?.needsRental) return [];
  const choices = active.map((r) => ({
    id: r.id,
    label: rentalLabel(r, now),
    ended: false,
  }));
  if (
    kind === 'open_rental' &&
    endedAnchor &&
    !choices.some((c) => c.id === endedAnchor.id)
  ) {
    choices.push({
      id: endedAnchor.id,
      label: rentalLabel(endedAnchor, now),
      ended: true,
    });
  }
  return choices;
}

/**
 * The rental the picker is on: `preferred` (the anchored rental at first,
 * then the agent's pick) when it is listed, else the rider's only active
 * rental, else none and the agent chooses.
 */
export function pickRentalId(choices: RentalChoice[], preferred: string): string {
  if (preferred && choices.some((c) => c.id === preferred)) return preferred;
  const active = choices.filter((c) => !c.ended);
  return active.length === 1 ? active[0].id : '';
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && Boolean(url.hostname);
  } catch (_) {
    return false;
  }
}

/** The draft's problems by field; empty when ant would take it. */
export function validateAction(draft: ActionDraft): ActionErrors {
  const errors: ActionErrors = {};
  const label = draft.label.trim();
  const value = draft.value.trim();
  const rentalId = draft.rentalId.trim();

  if (!label) errors.label = 'A button needs a label';
  else if (label.length > MAX_LABEL_LENGTH) {
    errors.label = `At most ${MAX_LABEL_LENGTH} characters`;
  }

  if (value.length > MAX_VALUE_LENGTH) {
    errors.value = `At most ${MAX_VALUE_LENGTH} characters`;
  } else if (draft.kind === 'open_url') {
    if (!value) errors.value = 'Enter the link to open';
    else if (!isHttpsUrl(value)) errors.value = 'Must be an https:// link';
  }

  if (actionKindInfo(draft.kind)?.needsRental) {
    if (!rentalId) errors.rentalId = 'This button needs the rental it acts on';
    else if (!UUID.test(rentalId)) errors.rentalId = 'Not a rental id';
  }
  return errors;
}

export const hasErrors = (errors: ActionErrors): boolean =>
  Object.keys(errors).length > 0;

/** The draft as ant takes it: only the fields its kind uses. */
export function toActionInput(draft: ActionDraft): ChatMessageActionInput {
  const info = actionKindInfo(draft.kind);
  const value = draft.value.trim();
  return {
    kind: draft.kind,
    label: draft.label.trim(),
    ...(draft.kind === 'reply' || draft.kind === 'open_url'
      ? { value: value || null }
      : {}),
    ...(info?.needsRental ? { rentalId: draft.rentalId.trim() } : {}),
  };
}
